import React, { useState, useEffect, useRef } from 'react';
import { forumApi } from '../../api/forum';
import TagBadge from './TagBadge';
import HrnbLoader from '../controls/HrnbLoader';
import '../../css/components/TagList.css';

/**
 * Список тегов — отображает популярные теги
 * @param {Object} props
 * @param {number} [props.limit=10] - Максимальное количество тегов
 * @param {Function} [props.onTagSelect] - Обработчик выбора тега
 * @param {string} [props.activeTag] - Активный тег (slug)
 */
export default function TagList({ limit = 10, onTagSelect, activeTag }) {
    const [tags, setTags] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const abortControllerRef = useRef(null);

    useEffect(() => {
        fetchTags();
        return () => {
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
        };
    }, []);

    const fetchTags = async () => {
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }
        abortControllerRef.current = new AbortController();

        try {
            setLoading(true);
            setError(null);
            const data = await forumApi.listTags();
            if (abortControllerRef.current.signal.aborted) return;
            setTags(data?.slice(0, limit) || []);
        } catch (err) {
            if (err.name === 'AbortError' || abortControllerRef.current?.signal.aborted) return;
            console.warn('[TagList] Failed to fetch tags:', err.message);
            setError('Не удалось загрузить теги');
            setTags([]);
        } finally {
            if (!abortControllerRef.current?.signal.aborted) {
                setLoading(false);
            }
        }
    };

    const handleTagClick = (tag) => {
        if (onTagSelect) {
            onTagSelect(tag);
        }
    };

    if (loading) {
        return <HrnbLoader size="sm" />;
    }

    if (error) {
        return (
            <div className="tag-list">
                <h5 className="tag-list-title">Популярные теги</h5>
                <div className="sidebar-widget-error">
                    <span className="sidebar-widget-error-text">{error}</span>
                    <button className="sidebar-widget-retry" onClick={fetchTags}>↻ Повторить</button>
                </div>
            </div>
        );
    }

    if (tags.length === 0) {
        return null;
    }

    return (
        <div className="tag-list">
            <h5 className="tag-list-title">Популярные теги</h5>
            <div className="tag-list-items">
                {tags.map((tag) => (
                    <TagBadge
                        key={tag.id || tag.slug}
                        tag={tag}
                        variant="default"
                        active={activeTag === (tag.slug || tag.name?.toLowerCase().replace(/\s+/g, '-'))}
                        onClick={handleTagClick}
                    />
                ))}
            </div>
        </div>
    );
}
